// Libraries
import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import MuiPopover from '@material-ui/core/Popover'
import { withStyles } from '@material-ui/core/styles'
import { Grid, Typography, IconButton } from '@material-ui/core'
import {
   ExitToAppOutlined,
   LockOutlined,
   EditOutlined,
   ErrorOutlineOutlined,
   AccountCircle
} from '@material-ui/icons'

// Components
import ButtonLink from 'components/commons/ButtonLink'
import MaterialIcon from 'components/materialIcon/materialIcon'
import UserAvatar from 'components/UserAvatar'

// Subcomponents
import ChangePasswordForm from './changePasswordForm'
import EditProfileImage from './editProfileImage'
import ProfileDetail from './profileDetails'

// Actions / Services / Utils
import { logoutUser } from 'actions/loginActions'
import { useStateValue } from 'context/store'
import { useGetUserSettings } from 'utils/customHooks/useGetUserSettings'
import TRANSLATE from 'utils/constants'

const Popover = withStyles({
   paper: {
      width: 'auto',
      height: 'auto',
      borderRadius: '4px',
      borderTop: '4px solid #1665D8',
      marginTop: 16,
      display: 'flex',
      flexDirection: 'column',
      '&>.policies': {
         bottom: 0,
         left: 0,
         textAlign: 'center',
         background: '#80808014',
         padding: '6px 0',
         color: '#1665D8',
         cursor: 'pointer',
         margin: '4px 5px',
         width: '97%',
         borderRadius: '0 0 4px 4px'
      },
      '&>.main': {
         height: 'auto',
         width: 320,
         padding: '24px 14px 0',
         '&>.avatar': {
            display: 'flex',
            paddingBottom: 16,
            '&>.avatar__image-container': {
               marginRight: 24,
               position: 'relative',
               cursor: 'pointer',
               '&>.avatar__avatar-container': {
                  width: 54,
                  height: 54,
                  boxShadow: '0px 1px 1px #00000029',
                  border: '4px solid #FFFFFF',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&>.avatar__img': {
                     width: 48,
                     height: 48
                  }
               },
               '&>.avatar__edit-icon-container': {
                  backgroundColor: '#3264DC',
                  color: '#FFF',
                  width: 4,
                  height: 4,
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  padding: 8,
                  '& .avatar__edit-icon': {
                     fontSize: 12
                  }
               }
            },
            '&>.avatar__info': {
               width: 194,
               height: 50,
               maxWidth: 194,
               '&>div': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
               },
               '&>.avatar__info-name': {
                  fontSize: 18,
                  fontWeight: 'bold',
                  textTransform: 'capitalize'
               },
               '&>.avatar__info-entity': {
                  fontSize: 14,
                  color: '#8e8e8e'
               }
            }
         },
         '&>.option': {
            height: 50,
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            paddingLeft: 10,
            cursor: 'pointer',
            '&:hover': {
               background: '#3264DC0D',
               color: '#3264DC'
            },
            '&>svg': {
               marginRight: 14
            }
         }
      },
      '&>.exit': {
         height: 'auto',
         width: 320,
         padding: '24px 14px',

         '&>.exit__icon': {
            '&>span': {
               fontSize: 38,
               color: '#43496A'
            }
         },
         '&>.exit__text': {
            fontSize: 14,
            textAlign: 'center',
            color: '#8A8FAB'
         },
         '&>.exit__buttons': {
            display: 'flex',
            width: '100%',
            justifyContent: 'space-evenly',
            marginTop: 8
         }
      }
   }
})(MuiPopover)

const UserProfilePopover = ({ anchorEl, setAnchorEl, history }) => {
   const open = Boolean(anchorEl)
   const id = open ? 'simple-popover' : undefined
   const [currentView, setCurrentView] = useState('profileView')

   const [{ sessionUser = {} }] = useStateValue()
   const userSettings = sessionUser.settings
   const userType = userSettings?.type

   const { userName, userEntity } = useGetUserSettings()

   function goToSitePolicies() {
      history.push('/site-policies')
   }

   function handleClose() {
      setAnchorEl(null)
   }

   useEffect(() => {
      if (anchorEl) {
         setCurrentView('profileView')
      }
   }, [anchorEl])

   return (
      <Popover
         id={id}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         transformOrigin={{ vertical: 'top', horizontal: 'right' }}
         open={open}
         anchorEl={anchorEl}
         onClose={handleClose}
      >
         {currentView === 'profileView' && (
            <>
               <UserProfile
                  setCurrentView={setCurrentView}
                  userType={userType}
                  userName={userName}
                  userEntity={userEntity}
               />
               <ButtonLink
                  text="POLÍTICAS DEL SITIO"
                  onClick={goToSitePolicies}
               />
            </>
         )}
         {currentView === 'changePasswordView' && (
            <ChangePasswordForm
               handleClose={handleClose}
               setParentView={setCurrentView}
            />
         )}
         {currentView === 'exitDialogView' && (
            <ExitDialog setCurrentView={setCurrentView} />
         )}
         {currentView === 'editProfileImageView' && (
            <EditProfileImage
               setParentView={setCurrentView}
               handleClose={handleClose}
               userName={userName}
               userEntity={userEntity}
            />
         )}
         {currentView === 'profileDetailView' && (
            <ProfileDetail setCurrentView={setCurrentView} />
         )}
      </Popover>
   )
}

export default withRouter(UserProfilePopover)

// -----------------------------------------------

function UserProfile({ setCurrentView, userType, userName, userEntity }) {
   function handleLogoutButtonClick() {
      setCurrentView('exitDialogView')
   }

   function handleEditProfileImage() {
      setCurrentView('editProfileImageView')
   }

   function gotoProfileDetails() {
      setCurrentView('profileDetailView')
   }

   function handleChangePasswordClick() {
      setCurrentView('changePasswordView')
   }

   return (
      <Grid className="main">
         <Grid className="avatar">
            <div
               className="avatar__image-container"
               onClick={handleEditProfileImage}
            >
               <div className="avatar__avatar-container">
                  <UserAvatar extraClass="avatar__img" />
               </div>
               <IconButton className="avatar__edit-icon-container">
                  <EditOutlined className="avatar__edit-icon" />
               </IconButton>
            </div>
            <Grid className="avatar__info">
               <Grid className="avatar__info-name">{userName}</Grid>
               <Grid className="avatar__info-entity">{userEntity}</Grid>
            </Grid>
         </Grid>
         <Grid
            className="option"
            onClick={() => {
               userType !== 'I' && handleChangePasswordClick()
            }}
         >
            <LockOutlined />
            {userType === 'I' ? (
               <a
                  style={{ textDecoration: 'none', color: '#43496A' }}
                  href="https://acceso.mutualser.com/authorization.do"
                  target="_blank"
                  rel="noreferrer"
               >
                  <Typography>Cambiar Contraseña</Typography>
               </a>
            ) : (
               <Typography>Cambiar Contraseña</Typography>
            )}
         </Grid>
         <Grid className="option" onClick={gotoProfileDetails}>
            <AccountCircle />
            <Typography>Perfil</Typography>
         </Grid>
         <Grid className="option">
            <ErrorOutlineOutlined />
            <a
               style={{ textDecoration: 'none', color: '#43496A' }}
               href="https://www.mutualser.com/index.php/ser-afiliado/pqrd?pb-slug=1"
               target="_blank"
               rel="noreferrer"
            >
               <Typography>PQRD</Typography>
            </a>
         </Grid>
         <Grid className="option" onClick={handleLogoutButtonClick}>
            <ExitToAppOutlined />
            <Typography>Salir</Typography>
         </Grid>
      </Grid>
   )
}

// -----------------------------------

function ExitDialog({ setCurrentView }) {
   const [{ sessionUser = {} }, dispatch] = useStateValue()

   const handleCancelExitButton = e => {
      setCurrentView('profileView')
   }

   const handleAcceptExitButton = e => {
      e.preventDefault()
      logoutUser(dispatch, sessionUser.token?.refresh_token, history)
      dispatch({ type: 'LOGOUT', authenticated: false })
   }

   return (
      <Grid
         className="exit"
         container
         direction="row"
         justify="center"
         alignItems="space-around"
      >
         <Grid
            className="exit__icon"
            container
            direction="col"
            justify="center"
            alignItems="center"
         >
            <MaterialIcon name="login" />
         </Grid>
         <Grid
            className="exit__text"
            container
            direction="row"
            justify="flex-end"
         >
            {TRANSLATE.FORM.EXIT_CONFIRMATION}
         </Grid>
         <Grid className="exit__buttons">
            <div>
               <ButtonLink text="CANCELAR" onClick={handleCancelExitButton} />
            </div>
            <div>
               <ButtonLink text="ACEPTAR" onClick={handleAcceptExitButton} />
            </div>
         </Grid>
      </Grid>
   )
}
